/**
 * Driver workload — the arithmetic behind "איזון עומס נהגים & ניטור משימות".
 *
 * The dashboard section answers one question a מ"פ asks a dozen times a shift:
 * *who has done the least, so who gets the next job*. Answering it honestly
 * means counting two things that are stored in completely different places:
 *
 * - **משימות מערכת** — the dispatched board. A mission is a {@link TaskTeam}
 *   that names the soldier in one of the two seats a task is *executed* from —
 *   נהג or מפקד משימה — on a task that was actually run: בביצוע or הושלמה, and
 *   not sitting in the recycle bin. A cancelled task was never driven and a
 *   binned one was never real, so neither counts. Both seats count because both
 *   are away on the job: crediting only the driver would show a soldier who
 *   commanded four convoys this week as free for the fifth.
 * - **רישום מהיר** — the {@link QuickDriverTask} rows a manager logged by hand
 *   for everything that never reached the board.
 *
 * The two are counted separately and *shown* separately — that is what the
 * stacked bar is for — because a driver with eight logged errands and no
 * dispatched convoy is in a very different position from one with eight convoys,
 * and a single total would hide it.
 *
 * Everything here is pure: no Dexie, no React, no clock of its own and no DOM.
 * The screen hands in the lists it already loaded and reads the rows and the
 * chart geometry back, so the numbers the graph draws are the numbers the
 * verification harness executes. The rules that already exist are not
 * re-implemented — a soldier's effective whereabouts come from
 * `./effective-status`, their job and display name from `./soldier-roles`, the
 * search from `./fleet-filters` and the recycle bin from `./recycle-bin`.
 */
import { EMPTY_ASSIGNMENT_INDEX, effectiveSoldierAvailability } from './effective-status';
import type { AssignmentIndex } from './effective-status';
import { matchesSoldierSearch, parseFleetSearch } from './fleet-filters';
import { soldierDisplayName, soldierRoleLabel, soldierRoleRank } from './soldier-roles';
import { isDeleted } from './recycle-bin';
import { normalizeTaskStatus } from '../types/task';
import { CREW_ROLE_LABELS } from '../types/assignment';
import { SOLDIER_AVAILABILITY_LABELS } from '../types/fleet';
import { QUICK_TASK_DESCRIPTION_MAX } from '../types/quick-task';
import type { Task } from '../types/task';
import type { TaskTeam } from '../types/assignment';
import type { Soldier, SoldierAvailability } from '../types/fleet';
import type { QuickDriverTask, QuickDriverTaskDraft } from '../types/quick-task';

// ── The quick form's own rules ───────────────────────────────────────

/**
 * A typed description, cleaned up: inner runs of whitespace collapsed (a
 * description is one line), trimmed, and cut to
 * {@link QUICK_TASK_DESCRIPTION_MAX}.
 */
export function normalizeQuickDescription(raw: string | null | undefined): string {
  return String(raw ?? '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, QUICK_TASK_DESCRIPTION_MAX);
}

/** Why the quick form will not submit yet — or `null` when it will. */
export type QuickTaskRefusal = 'noDriver' | 'noDescription';

export const QUICK_TASK_REFUSALS: Record<QuickTaskRefusal, string> = {
  noDriver: 'יש לבחור נהג מהרשימה',
  noDescription: 'יש לרשום מה בוצע',
};

/**
 * The two-field form's whole validation, in one place, so the button's disabled
 * state and the store's guard can never disagree about what "complete" means.
 */
export function quickTaskRefusal(
  draft: Partial<QuickDriverTaskDraft> | null | undefined,
): QuickTaskRefusal | null {
  if (!String(draft?.driverId ?? '').trim()) return 'noDriver';
  if (!normalizeQuickDescription(draft?.description)) return 'noDescription';
  return null;
}

// ── The driver picker ────────────────────────────────────────────────

/** One line of the searchable dropdown. */
export interface DriverCandidate {
  soldierId: string;
  /** The name alone — what the bar chart's axis is labelled with. */
  name: string;
  /** "נתי כהן - נהג" — the roster's own way of naming somebody. */
  displayName: string;
  roleLabel: string;
  /** Where they are right now, read through the effective status. */
  availability: SoldierAvailability;
  availabilityLabel: string;
}

/** Available first, then out on a mission, then away — the order to offer them in. */
const AVAILABILITY_ORDER: Readonly<Record<SoldierAvailability, number>> = {
  available: 0,
  on_mission: 1,
  leave: 2,
  unavailable: 3,
};

/**
 * The roster as the quick form offers it: the drivers who can take work first,
 * then the ones who are out, then the ones who are away.
 *
 * Nobody on the roster is *withheld*. A manager logging a journey is recording
 * something that already happened, and refusing to file it because the driver
 * has since gone on leave would simply lose the mission — so the availability
 * decides the order and the label beside each name, never whether the name is
 * offered. Within one availability the company's own reading order applies: rank
 * first (see `soldierRoleRank`), then the name.
 */
export function driverCandidates(
  soldiers: readonly Soldier[],
  index: AssignmentIndex = EMPTY_ASSIGNMENT_INDEX,
  now: number = Date.now(),
): DriverCandidate[] {
  const hierarchy = new Map(soldiers.map((soldier) => [soldier.id, soldierRoleRank(soldier.role)]));
  return soldiers
    .map((soldier) => {
      const availability = effectiveSoldierAvailability(soldier, index, now);
      const name = String(soldier.fullName ?? '').trim();
      const roleLabel = soldierRoleLabel(soldier.role);
      return {
        soldierId: soldier.id,
        name: name || roleLabel,
        displayName: soldierDisplayName(soldier),
        roleLabel,
        availability,
        availabilityLabel: SOLDIER_AVAILABILITY_LABELS[availability],
      };
    })
    .sort(
      (a, b) =>
        AVAILABILITY_ORDER[a.availability] - AVAILABILITY_ORDER[b.availability] ||
        (hierarchy.get(a.soldierId) ?? 0) - (hierarchy.get(b.soldierId) ?? 0) ||
        a.name.localeCompare(b.name, 'he'),
    );
}

/**
 * The candidates matching what was typed into the autocomplete.
 *
 * The match itself is the roster's own — `matchesSoldierSearch()` — so typing a
 * personal number, a phone or a platoon into the quick form finds exactly what
 * typing it into the roster's search box finds. An empty box matches everybody,
 * which is what makes the field a dropdown as well as a search.
 */
export function searchDriverCandidates(
  candidates: readonly DriverCandidate[],
  soldiers: readonly Soldier[],
  raw: string | null | undefined,
): DriverCandidate[] {
  const query = parseFleetSearch(raw);
  if (!query.text) return [...candidates];
  const roster = new Map(soldiers.map((soldier) => [soldier.id, soldier]));
  return candidates.filter((candidate) => {
    const soldier = roster.get(candidate.soldierId);
    return soldier ? matchesSoldierSearch(soldier, query) : false;
  });
}

// ── Counting the two kinds of mission ────────────────────────────────

/**
 * The tasks a driver's mission can be counted against: run or finished, and not
 * in the recycle bin. A cancelled task was never driven.
 */
export function countableTasks(tasks: readonly Task[]): Task[] {
  return tasks.filter(
    (task) => !isDeleted(task) && normalizeTaskStatus(task.statusId) !== 'cancelled',
  );
}

/**
 * The two seats a task is executed from, and therefore the two that put a
 * soldier on the workload graph. `escort` is the stored spelling of
 * מפקד משימה — see the note in `src/types/assignment.ts`.
 */
export const WORKLOAD_SEATS = ['driver', 'commander'] as const;

export type WorkloadSeat = (typeof WORKLOAD_SEATS)[number];

/** How each seat is named on the graph and in the ranking list. */
export const WORKLOAD_SEAT_LABELS: Record<WorkloadSeat, string> = {
  driver: CREW_ROLE_LABELS.driver,
  commander: CREW_ROLE_LABELS.escort,
};

/** One soldier's dispatched missions, and which seats they were run from. */
export interface CrewMissionCount {
  /** Distinct tasks the soldier was out on, in either seat. */
  total: number;
  asDriver: number;
  asCommander: number;
}

/**
 * How many dispatched missions each crew member has, keyed by soldier id.
 *
 * Counted per *task*, not per team: a driver who was given two vehicles on one
 * convoy made one journey, and counting the seat twice would put them at the top
 * of the graph for a job somebody else did half of. The same rule spans the two
 * seats — somebody who drove one vehicle and commanded another on the same
 * convoy was out once, so `total` counts them once even though `asDriver` and
 * `asCommander` each claim that task.
 */
export function systemMissionsByCrew(
  teams: readonly TaskTeam[],
  tasks: readonly Task[],
): Map<string, CrewMissionCount> {
  const countable = new Set(countableTasks(tasks).map((task) => task.id));
  const seen = new Map<string, Record<WorkloadSeat | 'all', Set<string>>>();

  const credit = (raw: string | undefined, taskId: string, seat: WorkloadSeat): void => {
    const soldierId = String(raw ?? '').trim();
    if (!soldierId) return;
    const tally = seen.get(soldierId) ?? {
      all: new Set<string>(),
      driver: new Set<string>(),
      commander: new Set<string>(),
    };
    tally.all.add(taskId);
    tally[seat].add(taskId);
    seen.set(soldierId, tally);
  };

  for (const team of teams) {
    if (!countable.has(team.taskId)) continue;
    credit(team.driverId, team.taskId, 'driver');
    // Only a named commander is out on the job: `normalizeTeam()` clears
    // `escortId` for "ללא מפקד משימה" and "טרם נקבע", and asking for the
    // decision as well means a stale id could never reach the graph either.
    if (team.escortOption === 'soldier') credit(team.escortId, team.taskId, 'commander');
  }

  const counts = new Map<string, CrewMissionCount>();
  for (const [soldierId, tally] of seen) {
    counts.set(soldierId, {
      total: tally.all.size,
      asDriver: tally.driver.size,
      asCommander: tally.commander.size,
    });
  }
  return counts;
}

/** Nobody's missions — what the graph reads for a soldier with no team. */
export const NO_MISSIONS: CrewMissionCount = { total: 0, asDriver: 0, asCommander: 0 };

/** How many hand-logged missions each driver has, keyed by soldier id. */
export function quickMissionsByDriver(
  quickTasks: readonly QuickDriverTask[],
): Map<string, number> {
  const counts = new Map<string, number>();
  for (const entry of quickTasks) {
    const driverId = String(entry.driverId ?? '').trim();
    if (!driverId) continue;
    counts.set(driverId, (counts.get(driverId) ?? 0) + 1);
  }
  return counts;
}

// ── The rank a manager reads off the row ─────────────────────────────

export const WORKLOAD_RANKS = ['busiest', 'balanced', 'available'] as const;

export type WorkloadRank = (typeof WORKLOAD_RANKS)[number];

export const WORKLOAD_RANK_LABELS: Record<WorkloadRank, string> = {
  busiest: 'הכי פעיל',
  balanced: 'עומס רגיל',
  available: 'זמין למשימה',
};

/** How each rank is drawn — high contrast, and never colour alone. */
export const WORKLOAD_RANK_CLASSES: Record<WorkloadRank, string> = {
  busiest: 'border-rose-300 bg-rose-50 text-rose-700',
  balanced: 'border-line bg-canvas text-ink/70',
  available: 'border-emerald-300 bg-emerald-50 text-emerald-700',
};

/**
 * Where one total sits against the rest of the roster.
 *
 * Deliberately relative rather than absolute: "busy" on a quiet Tuesday is four
 * missions and on an exercise it is fourteen, and a manager balancing the board
 * is comparing drivers with each other, never against a number somebody hard-coded.
 *
 * The lightest load is checked first on purpose. When every driver carries the
 * same amount — most obviously at the start of a day, when everyone is on zero —
 * they are all equally free to take the next job, and reading the whole roster as
 * "הכי פעיל" would be exactly backwards.
 */
export function workloadRank(total: number, lightest: number, heaviest: number): WorkloadRank {
  if (total <= lightest) return 'available';
  if (total >= heaviest) return 'busiest';
  return 'balanced';
}

// ── The rows the section renders ─────────────────────────────────────

/** One driver's line: the two counts, their sum, and where it puts them. */
export interface DriverWorkloadRow {
  soldierId: string;
  name: string;
  displayName: string;
  roleLabel: string;
  availability: SoldierAvailability;
  availabilityLabel: string;
  /** Dispatched missions off the board, either seat, counted once per task. */
  systemTasks: number;
  /** Of those, the ones run from the driver's seat. */
  driverMissions: number;
  /** Of those, the ones run as מפקד משימה. */
  commanderMissions: number;
  /** "2 כנהג · 1 כמפקד משימה", or `''` when the board credits them nothing. */
  seatBreakdown: string;
  /** Hand-logged missions. */
  quickTasks: number;
  /** The two added together — what the bar's height is. */
  total: number;
  rank: WorkloadRank;
  rankLabel: string;
}

export interface DriverWorkloadInput {
  soldiers: readonly Soldier[];
  teams?: readonly TaskTeam[];
  tasks?: readonly Task[];
  quickTasks?: readonly QuickDriverTask[];
  /** Who is out right now — the shell already derived it once. */
  assignments?: AssignmentIndex;
  now?: number;
}

/**
 * "2 כנהג · 1 כמפקד משימה" — how a row's dispatched missions split across the
 * two seats. A seat nobody sat in is left out rather than written as a zero, and
 * a soldier the board credits nothing gets an empty string, so the ranking list
 * never carries a phrase with no information in it.
 */
export function describeSeats(count: CrewMissionCount): string {
  return [
    count.asDriver > 0 ? `${count.asDriver} כ${WORKLOAD_SEAT_LABELS.driver}` : '',
    count.asCommander > 0 ? `${count.asCommander} כ${WORKLOAD_SEAT_LABELS.commander}` : '',
  ]
    .filter(Boolean)
    .join(' · ');
}

export interface DriverWorkloadSummary {
  rows: DriverWorkloadRow[];
  /** Every dispatched mission on the roster. */
  systemTotal: number;
  /** Of those, the ones run from the driver's seat. */
  driverTotal: number;
  /** Of those, the ones run as מפקד משימה. */
  commanderTotal: number;
  /** Every hand-logged mission on the roster. */
  quickTotal: number;
  total: number;
  /** The heaviest single load — what the chart's axis is scaled to. */
  heaviest: number;
  lightest: number;
  /** The drivers a manager should reach for first, lightest load leading. */
  nextUp: DriverWorkloadRow[];
}

/** How many names the "who is free" line offers before it stops. */
export const NEXT_UP_LIMIT = 3;

/**
 * The whole section's numbers, derived in one pass.
 *
 * Every soldier on the roster gets a row, including the ones on nothing: a
 * driver with no missions is the single most useful row on the screen, and
 * dropping them for having an empty bar would take the answer off the graph.
 *
 * Ordered heaviest first — the graph reads as a ranking — with the roster's own
 * order breaking a tie so two drivers on the same load never swap places between
 * renders.
 */
export function buildDriverWorkload(input: DriverWorkloadInput): DriverWorkloadSummary {
  const now = input.now ?? Date.now();
  const teams = input.teams ?? [];
  const tasks = input.tasks ?? [];
  const quickTasks = input.quickTasks ?? [];

  const system = systemMissionsByCrew(teams, tasks);
  const quick = quickMissionsByDriver(quickTasks);
  const candidates = driverCandidates(input.soldiers, input.assignments, now);

  const counted = candidates.map((candidate) => {
    const seats = system.get(candidate.soldierId) ?? NO_MISSIONS;
    const quickCount = quick.get(candidate.soldierId) ?? 0;
    return { candidate, seats, quickTasks: quickCount, total: seats.total + quickCount };
  });

  const totals = counted.map((row) => row.total);
  const heaviest = totals.length > 0 ? Math.max(...totals) : 0;
  const lightest = totals.length > 0 ? Math.min(...totals) : 0;

  /** The roster's own position, used to break a tie on the load. */
  const rosterOrder = new Map(candidates.map((candidate, at) => [candidate.soldierId, at]));
  const byRoster = (a: { soldierId: string }, b: { soldierId: string }): number =>
    (rosterOrder.get(a.soldierId) ?? 0) - (rosterOrder.get(b.soldierId) ?? 0);

  const rows: DriverWorkloadRow[] = counted
    .map(({ candidate, seats, quickTasks: quickCount, total }) => {
      const rank = workloadRank(total, lightest, heaviest);
      return {
        soldierId: candidate.soldierId,
        name: candidate.name,
        displayName: candidate.displayName,
        roleLabel: candidate.roleLabel,
        availability: candidate.availability,
        availabilityLabel: candidate.availabilityLabel,
        systemTasks: seats.total,
        driverMissions: seats.asDriver,
        commanderMissions: seats.asCommander,
        seatBreakdown: describeSeats(seats),
        quickTasks: quickCount,
        total,
        rank,
        rankLabel: WORKLOAD_RANK_LABELS[rank],
      };
    })
    .sort((a, b) => b.total - a.total || byRoster(a, b));

  // Who to hand the next job to: the lightest loads, and among equal loads the
  // drivers who are actually standing by rather than out on a convoy.
  const nextUp = [...rows]
    .sort(
      (a, b) =>
        a.total - b.total ||
        AVAILABILITY_ORDER[a.availability] - AVAILABILITY_ORDER[b.availability] ||
        byRoster(a, b),
    )
    .slice(0, NEXT_UP_LIMIT);

  return {
    rows,
    systemTotal: rows.reduce((sum, row) => sum + row.systemTasks, 0),
    driverTotal: rows.reduce((sum, row) => sum + row.driverMissions, 0),
    commanderTotal: rows.reduce((sum, row) => sum + row.commanderMissions, 0),
    quickTotal: rows.reduce((sum, row) => sum + row.quickTasks, 0),
    total: rows.reduce((sum, row) => sum + row.total, 0),
    heaviest,
    lightest,
    nextUp,
  };
}

// ── The chart, as geometry ───────────────────────────────────────────

/**
 * The two series, named once. The colours are stated here rather than in the
 * component because the component draws SVG, and an SVG `fill` cannot be a
 * Tailwind class — keeping them beside the data is what stops the legend and the
 * bars from drifting apart.
 *
 * Blue is the dispatched board (the brand's own `--color-brand-500`) and amber
 * is the hand-logged pile, exactly as the mandate names them. Both are dark
 * enough against the white panel to clear the contrast bar on their own, and the
 * legend spells each one out — the chart is never read by colour alone.
 */
export const WORKLOAD_SERIES = [
  { key: 'system', label: 'משימות מערכת', color: '#1e88e5' },
  { key: 'quick', label: 'רישום מהיר', color: '#f59e0b' },
] as const;

export type WorkloadSeriesKey = (typeof WORKLOAD_SERIES)[number]['key'];

/** One stacked bar, positioned. */
export interface WorkloadChartBar {
  soldierId: string;
  name: string;
  /** The full sentence a screen reader and a hover both get. */
  title: string;
  x: number;
  width: number;
  total: number;
  rank: WorkloadRank;
  segments: {
    key: WorkloadSeriesKey;
    color: string;
    value: number;
    y: number;
    height: number;
  }[];
  /** Where the name and the total are anchored, horizontally centred on the bar. */
  centerX: number;
  /** The top of the stack — where the total is written. */
  topY: number;
}

export interface WorkloadChart {
  width: number;
  height: number;
  plot: { x: number; y: number; width: number; height: number; bottom: number };
  /** The value the axis tops out at — never zero, so nothing divides by it. */
  scaleMax: number;
  ticks: { value: number; y: number }[];
  bars: WorkloadChartBar[];
}

export interface WorkloadChartOptions {
  /** Horizontal room per driver, bar plus gutter. */
  slot?: number;
  barWidth?: number;
  height?: number;
}

/** Two decimals is plenty for a coordinate, and keeps the geometry comparable. */
const round = (value: number): number => Math.round(value * 100) / 100;

const CHART_PADDING = { top: 22, right: 34, bottom: 84, left: 12 } as const;

/**
 * Turns the rows into the exact coordinates the SVG draws, right-to-left so the
 * heaviest driver sits under the section's own right-hand edge and the chart is
 * read in the same direction as the page around it.
 *
 * The width grows with the roster rather than the bars shrinking into it — a
 * twenty-name roster squeezed into a phone's width is a stripe pattern, not a
 * chart. The component scrolls it instead, so every driver keeps a readable bar
 * and none is silently dropped.
 */
export function buildWorkloadChart(
  rows: readonly DriverWorkloadRow[],
  options: WorkloadChartOptions = {},
): WorkloadChart {
  const slot = options.slot ?? 52;
  const barWidth = options.barWidth ?? Math.min(30, slot - 16);
  const height = options.height ?? 260;

  const plotHeight = Math.max(1, height - CHART_PADDING.top - CHART_PADDING.bottom);
  const plotWidth = Math.max(slot, rows.length * slot);
  const width = plotWidth + CHART_PADDING.left + CHART_PADDING.right;
  const plot = {
    x: CHART_PADDING.left,
    y: CHART_PADDING.top,
    width: plotWidth,
    height: plotHeight,
    bottom: CHART_PADDING.top + plotHeight,
  };

  // An empty board must still draw an axis, so the scale never falls to zero.
  const heaviest = rows.reduce((max, row) => Math.max(max, row.total), 0);
  const scaleMax = Math.max(1, heaviest);
  const unit = plotHeight / scaleMax;

  const tickValues = [...new Set([0, Math.round(scaleMax / 2), scaleMax])].sort((a, b) => a - b);
  const ticks = tickValues.map((value) => ({
    value,
    y: round(plot.bottom - value * unit),
  }));

  const bars = rows.map((row, position) => {
    // Right-to-left: the first row takes the right-hand slot.
    const x = round(plot.x + plotWidth - (position + 1) * slot + (slot - barWidth) / 2);
    const systemHeight = round(row.systemTasks * unit);
    const quickHeight = round(row.quickTasks * unit);
    const systemY = round(plot.bottom - systemHeight);
    const quickY = round(systemY - quickHeight);
    return {
      soldierId: row.soldierId,
      name: row.name,
      // The seats are named in the sentence too: a bar the board credits to a
      // מפקד משימה has to say so, or the graph looks like it counted a driver.
      title: [
        `${row.displayName} — ${row.total} משימות`,
        row.seatBreakdown ? `${row.systemTasks} מערכת (${row.seatBreakdown})` : `${row.systemTasks} מערכת`,
        `${row.quickTasks} רישום מהיר`,
        row.rankLabel,
      ].join(' · '),
      x,
      width: barWidth,
      total: row.total,
      rank: row.rank,
      segments: [
        { key: 'system' as const, color: WORKLOAD_SERIES[0].color, value: row.systemTasks, y: systemY, height: systemHeight },
        { key: 'quick' as const, color: WORKLOAD_SERIES[1].color, value: row.quickTasks, y: quickY, height: quickHeight },
      ].filter((segment) => segment.value > 0),
      centerX: round(x + barWidth / 2),
      topY: round(quickY),
    };
  });

  return { width: round(width), height, plot, scaleMax, ticks, bars };
}

// ── The audit line the section shows ─────────────────────────────────

/**
 * "נרשם ע״י אשר אמר · 16:05 — נסיעה לקצין עיר לנתי כהן".
 *
 * The clock is the row's own `createdAt`, so the line always names the moment the
 * mission was filed rather than "now". Formatted by `./audit-stamp`'s rule — the
 * same one every other record on the screen is stamped with — through the
 * caller, which hands the already-formatted time in.
 */
export function describeQuickTask(entry: QuickDriverTask, clock: string): string {
  return `נרשם ע״י ${entry.createdByName} · ${clock} — ${entry.description} (${entry.driverName})`;
}
